import { useState } from "react";
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import type { ReverseFormData } from "./ReverseForm";

interface ReverseCostBreakdown {
  repairCost: number;
  evictionCost: number;
  acquisitionTax: number;
  financeCost: number;
  mgmtFee: number;
  brokerageFee: number;
  extraCost: number;
  totalCost: number;
}

export interface ReverseResults {
  maxBidPrice: number;     // P = V - M - C - R
  safeBid: number;         // P × 0.95
  aggressiveBid: number;   // P
  costs: ReverseCostBreakdown;
  effectiveROI: number;    // 실질 ROI at maxBidPrice
  costRatio: number;       // 총비용 / 급매시세
  isSafe: boolean;         // maxBidPrice > minimumBidPrice
  isOverMinBid: boolean;   // maxBidPrice < minimumBidPrice 경고
  sensitivityMatrix: SensitivityRow[]; // 민감도 분석
  dangerLineViolation: boolean; // 마지노선 초과 여부
}

interface SensitivityRow {
  priceChangePercent: number;
  adjustedMarketPrice: number;
  maxBidAtScenario: number;
  roi: number;
  isProfit: boolean;
}

function getAgeWeight(age: number): number {
  if (age <= 5) return 0.5;
  if (age <= 10) return 1.0;
  if (age <= 20) return 1.5;
  return 2.0;
}

function getEvictionCost(area: number, occupantType: string): number {
  switch (occupantType) {
    case "debtor": return Math.max(2000000, area * 30000);
    case "tenant_no_rights": return Math.max(3000000, area * 50000);
    case "tenant_with_rights": return Math.max(10000000, area * 120000);
    case "lien": return Math.max(15000000, area * 150000);
    default: return 0;
  }
}

function getTaxRate(homeCount: number, price: number): number {
  if (homeCount <= 1) {
    if (price <= 600000000) return 0.011;
    if (price <= 900000000) return 0.013;
    return 0.035;
  }
  if (homeCount === 2) return 0.08;
  return 0.12;
}

export function calculateReverse(data: ReverseFormData): ReverseResults {
  const {
    exclusiveArea, buildingAge, acuteMarketPrice, minimumBidPrice,
    targetMargin, takeoverAmount, occupantType, homeCount,
    loanAmount, loanRate, loanDuration, unpaidMgmtFee, brokerageFee, extraCost,
  } = data;

  const repairCost = Math.round(exclusiveArea * 50000 * getAgeWeight(buildingAge));
  const evictionCost = getEvictionCost(exclusiveArea, occupantType);
  const taxRate = getTaxRate(homeCount, acuteMarketPrice);
  const acquisitionTax = Math.round(acuteMarketPrice * taxRate);
  const financeCost = Math.round(loanAmount * (loanRate / 100) * (loanDuration / 12));
  const mgmtFee = unpaidMgmtFee > 0 ? unpaidMgmtFee : Math.round(exclusiveArea * 20000);

  const totalCost = repairCost + evictionCost + acquisitionTax + financeCost + mgmtFee + brokerageFee + extraCost;

  // 핵심 공식: P = V - M - C - R
  const maxBidPrice = acuteMarketPrice - targetMargin - totalCost - takeoverAmount;

  const safeBid = Math.round(maxBidPrice * 0.95);
  const aggressiveBid = maxBidPrice;

  const effectiveROI = maxBidPrice > 0
    ? Math.round(((acuteMarketPrice - takeoverAmount - totalCost - maxBidPrice) / maxBidPrice) * 100)
    : 0;

  const costRatio = acuteMarketPrice > 0
    ? Math.round((totalCost / acuteMarketPrice) * 100)
    : 0;

  const isSafe = maxBidPrice > 0 && maxBidPrice >= minimumBidPrice;
  const isOverMinBid = maxBidPrice < minimumBidPrice;
  const dangerLineViolation = maxBidPrice < 0;

  // 민감도 분석: -10% ~ +10% 시나리오
  const sensitivityMatrix: SensitivityRow[] = [-10, -5, 0, +5, +10].map((pct) => {
    const adj = Math.round(acuteMarketPrice * (1 + pct / 100));
    const adjTax = Math.round(adj * taxRate);
    const adjTotalCost = repairCost + evictionCost + adjTax + financeCost + mgmtFee + brokerageFee + extraCost;
    const adjMax = adj - targetMargin - adjTotalCost - takeoverAmount;
    const adjROI = adjMax > 0
      ? Math.round(((adj - takeoverAmount - adjTotalCost - adjMax) / adjMax) * 100)
      : -999;
    return {
      priceChangePercent: pct,
      adjustedMarketPrice: adj,
      maxBidAtScenario: adjMax,
      roi: adjROI,
      isProfit: adjMax > 0 && adjROI >= 0,
    };
  });

  return {
    maxBidPrice,
    safeBid,
    aggressiveBid,
    costs: { repairCost, evictionCost, acquisitionTax, financeCost, mgmtFee, brokerageFee, extraCost, totalCost },
    effectiveROI,
    costRatio,
    isSafe,
    isOverMinBid,
    dangerLineViolation,
    sensitivityMatrix,
  };
}

function fmt(n: number): string {
  if (n < 0) return "-" + Math.abs(n).toLocaleString() + "원";
  return n.toLocaleString() + "원";
}

function fmtShort(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 100000000) return sign + (abs / 100000000).toFixed(1) + "억";
  if (abs >= 10000) return sign + Math.round(abs / 10000) + "만";
  return fmt(n);
}

interface Props {
  results: ReverseResults;
  data: ReverseFormData;
  onTargetMarginChange?: (v: number) => void;
}

export default function ReverseResult({ results, data, onTargetMarginChange }: Props) {
  const [showSensitivity, setShowSensitivity] = useState(false);
  const {
    maxBidPrice, safeBid, aggressiveBid, costs, effectiveROI,
    costRatio, isOverMinBid, dangerLineViolation, sensitivityMatrix,
  } = results;

  const taxRate = getTaxRate(data.homeCount, data.acuteMarketPrice);

  // 실시간 슬라이더: 목표마진 변경 → 투찰 상한가 역산
  const sliderMin = 0;
  const sliderMax = Math.round(data.acuteMarketPrice * 0.4);
  const sliderStep = 1000000;

  return (
    <div className="space-y-5">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#1C1917]">역산 결과</h2>
          <p className="text-xs text-[#8C8780] mt-0.5">시세역산방식 — P = V - M - C - R</p>
        </div>
        <span className="px-2.5 py-1 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-full">
          실시간
        </span>
      </div>

      {/* 위험 경고 — 마지노선 초과 */}
      {dangerLineViolation && (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-rose-50 border-2 border-rose-400 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-rose-500 flex items-center justify-center flex-shrink-0">
              <i className="ri-alarm-warning-line text-white"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-rose-700 mb-1">투찰 불가 — 마지노선 초과</p>
              <p className="text-xs text-rose-600 leading-relaxed">
                현재 목표 수익({data.targetMargin.toLocaleString()}원)과 비용 합계가 급매 시세를 초과합니다.
                목표 수익을 낮추거나 비용을 절감하지 않으면 어떤 금액으로 입찰해도 <strong>반드시 손실</strong>이 발생합니다.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* 최저가 미달 경고 */}
      {!dangerLineViolation && isOverMinBid && (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-amber-50 border border-amber-300 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <i className="ri-error-warning-line text-amber-500 text-lg mt-0.5"></i>
            <div>
              <p className="text-xs font-bold text-amber-700 mb-0.5">투찰 상한가 ≤ 최저 입찰가</p>
              <p className="text-xs text-amber-600">
                역산 투찰 상한가({fmtShort(maxBidPrice)})가 법원 최저입찰가({fmtShort(data.minimumBidPrice)})보다 낮습니다.
                현재 조건으로는 입찰 자체가 불가능하거나 낙찰 가능성이 매우 낮습니다.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* 핵심 결과 카드 */}
      <div className="space-y-3">
        {/* 투찰 상한가 (P_max) */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-xl border-2 p-5 ${
            dangerLineViolation
              ? "bg-rose-50 border-rose-300"
              : "bg-gradient-to-br from-[#1C1917] to-[#3A2E28] border-[#C4956A]/30"
          }`}
        >
          <div className="flex items-center gap-2.5 mb-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${dangerLineViolation ? "bg-rose-500" : "bg-[#C4956A]"}`}>
              <i className="ri-arrow-up-circle-line text-white text-sm"></i>
            </div>
            <div>
              <span className={`text-sm font-bold ${dangerLineViolation ? "text-rose-700" : "text-white"}`}>
                투찰 상한가 (P)
              </span>
              <span className={`text-[10px] ml-1.5 uppercase tracking-wide ${dangerLineViolation ? "text-rose-400" : "text-[#B8B3AE]"}`}>
                = V - M - C - R
              </span>
            </div>
          </div>
          <p className={`text-3xl font-bold mb-1 ${dangerLineViolation ? "text-rose-600" : "text-white"}`}>
            {fmt(maxBidPrice)}
          </p>
          {!dangerLineViolation && (
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/10">
              <div className="text-center">
                <p className="text-[10px] text-[#B8B3AE]">보수적</p>
                <p className="text-sm font-bold text-[#C4956A]">{fmtShort(safeBid)}</p>
                <p className="text-[10px] text-[#8C8780]">P × 0.95</p>
              </div>
              <div className="flex-1 h-px bg-white/10"></div>
              <div className="text-center">
                <p className="text-[10px] text-[#B8B3AE]">상한</p>
                <p className="text-sm font-bold text-white">{fmtShort(aggressiveBid)}</p>
                <p className="text-[10px] text-[#8C8780]">P (한계선)</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* 수익성 요약 */}
        {!dangerLineViolation && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-3 gap-2"
          >
            <div className="bg-white border border-[#E8E5E0] rounded-xl p-3 text-center">
              <p className="text-[10px] text-[#8C8780] mb-1">목표 순수익</p>
              <p className="text-sm font-bold text-[#1C1917]">{fmtShort(data.targetMargin)}</p>
            </div>
            <div className="bg-white border border-[#E8E5E0] rounded-xl p-3 text-center">
              <p className="text-[10px] text-[#8C8780] mb-1">실질 ROI</p>
              <p className={`text-sm font-bold ${effectiveROI >= 10 ? "text-emerald-600" : effectiveROI >= 0 ? "text-[#C4956A]" : "text-rose-500"}`}>
                {effectiveROI >= 0 ? "+" : ""}{effectiveROI}%
              </p>
            </div>
            <div className="bg-white border border-[#E8E5E0] rounded-xl p-3 text-center">
              <p className="text-[10px] text-[#8C8780] mb-1">비용 비율</p>
              <p className="text-sm font-bold text-[#8C8780]">{costRatio}%</p>
            </div>
          </motion.div>
        )}
      </div>

      {/* 실시간 역산 슬라이더 (Track B: 투자자 툴) */}
      {onTargetMarginChange && (
        <div className="bg-white rounded-xl border border-[#E8E5E0] p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold text-[#1C1917] flex items-center gap-1.5">
              <i className="ri-contrast-2-line text-[#C4956A]"></i>
              목표 수익 슬라이더 (역산)
            </h4>
            <span className="text-xs font-semibold text-[#C4956A]">
              {fmtShort(data.targetMargin)}
            </span>
          </div>
          <input
            type="range"
            min={sliderMin}
            max={sliderMax}
            step={sliderStep}
            value={data.targetMargin}
            onChange={(e) => onTargetMarginChange(parseInt(e.target.value, 10))}
            className="w-full accent-[#C4956A] cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-[#B8B3AE] mt-1">
            <span>0원</span>
            <span>← 슬라이더 조절 시 투찰 상한가 실시간 역산 →</span>
            <span>{fmtShort(sliderMax)}</span>
          </div>
          {!dangerLineViolation && (
            <div className="mt-3 bg-[#FAFAF8] rounded-lg p-3 text-center">
              <p className="text-[10px] text-[#8C8780]">현재 목표 수익 기준 투찰 상한가</p>
              <p className="text-lg font-bold text-[#1C1917]">{fmt(maxBidPrice)}</p>
            </div>
          )}
        </div>
      )}

      {/* 부대비용 상세 */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5">
        <h4 className="text-xs font-bold text-[#1C1917] mb-4 flex items-center gap-1.5">
          <i className="ri-file-list-3-line text-[#C4956A]"></i>
          제반 비용 상세 (C)
        </h4>
        <div className="space-y-0">
          {[
            { label: "수리비", value: costs.repairCost, info: `${data.exclusiveArea}m² × 5만원 × 가중치 ${getAgeWeight(data.buildingAge)}` },
            { label: "명도비", value: costs.evictionCost, info: "점유자 유형별 추정" },
            { label: `취득세 (${(taxRate * 100).toFixed(1)}%)`, value: costs.acquisitionTax, info: `${data.homeCount <= 1 ? "1주택 이하" : data.homeCount + "주택"} 기준` },
            { label: "금융비용", value: costs.financeCost, info: `${fmtShort(data.loanAmount)} × ${data.loanRate}% × ${data.loanDuration}개월` },
            { label: "미납 관리비", value: costs.mgmtFee, info: "직접 입력 또는 자동 추정" },
            { label: "중개수수료", value: costs.brokerageFee, info: "" },
            { label: "기타 비용", value: costs.extraCost, info: "등기비 등" },
          ].map((item, idx, arr) => (
            <div
              key={idx}
              className={`flex items-center justify-between py-2.5 ${idx < arr.length - 1 ? "border-b border-[#F0EDE8]" : ""}`}
            >
              <div>
                <span className="text-xs font-medium text-[#5C5650]">{item.label}</span>
                {item.info && <p className="text-[10px] text-[#B8B3AE]">{item.info}</p>}
              </div>
              <span className="text-xs font-semibold text-[#1C1917] whitespace-nowrap">{fmt(item.value)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between py-3 mt-1 border-t-2 border-[#1C1917]">
            <span className="text-sm font-bold text-[#1C1917]">합계 (C)</span>
            <span className="text-sm font-bold text-[#1C1917]">{fmt(costs.totalCost)}</span>
          </div>
        </div>
      </div>

      {/* 역산 구조 시각화 */}
      {!dangerLineViolation && (
        <div className="bg-white rounded-xl border border-[#E8E5E0] p-5">
          <h4 className="text-xs font-bold text-[#1C1917] mb-4 flex items-center gap-1.5">
            <i className="ri-bar-chart-horizontal-line text-[#C4956A]"></i>
            시세 역산 구조
          </h4>
          <div className="space-y-2">
            {[
              { label: "급매 시세 (V)", value: data.acuteMarketPrice, color: "bg-emerald-500", pct: 100 },
              { label: "목표 순수익 (M)", value: data.targetMargin, color: "bg-[#C4956A]", pct: Math.round((data.targetMargin / data.acuteMarketPrice) * 100) },
              { label: "제반 비용 (C)", value: costs.totalCost, color: "bg-amber-400", pct: Math.round((costs.totalCost / data.acuteMarketPrice) * 100) },
              { label: "인수 권리금 (R)", value: data.takeoverAmount, color: "bg-rose-400", pct: Math.round((data.takeoverAmount / data.acuteMarketPrice) * 100) },
              { label: "투찰 상한가 (P)", value: maxBidPrice, color: "bg-[#1C1917]", pct: Math.round((maxBidPrice / data.acuteMarketPrice) * 100) },
            ].map((row, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-24 flex-shrink-0">
                  <p className="text-[10px] text-[#8C8780] leading-tight">{row.label}</p>
                </div>
                <div className="flex-1 h-5 bg-[#F5F2EE] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${row.color} transition-all duration-500`}
                    style={{ width: `${Math.max(0, Math.min(100, row.pct))}%` }}
                  ></div>
                </div>
                <div className="w-20 flex-shrink-0 text-right">
                  <p className="text-[10px] font-semibold text-[#1C1917]">{fmtShort(row.value)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 민감도 분석 (시세 변동 시나리오) */}
      <div className="bg-white rounded-xl border border-[#E8E5E0] p-5">
        <button
          onClick={() => setShowSensitivity(!showSensitivity)}
          className="w-full flex items-center justify-between cursor-pointer"
        >
          <h4 className="text-xs font-bold text-[#1C1917] flex items-center gap-1.5">
            <i className="ri-grid-line text-[#C4956A]"></i>
            시세 변동 민감도 분석
            <span className="px-1.5 py-0.5 bg-[#C4956A]/10 text-[#C4956A] text-[10px] rounded font-normal ml-1">
              ±10% 시나리오
            </span>
          </h4>
          <i className={`ri-${showSensitivity ? "subtract" : "add"}-line text-[#8C8780]`}></i>
        </button>

        {showSensitivity && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-4"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#F0EDE8]">
                    <th className="text-left py-2 text-[10px] text-[#8C8780] font-medium">시세 변동</th>
                    <th className="text-right py-2 text-[10px] text-[#8C8780] font-medium">조정 시세</th>
                    <th className="text-right py-2 text-[10px] text-[#8C8780] font-medium">투찰 상한가</th>
                    <th className="text-right py-2 text-[10px] text-[#8C8780] font-medium">ROI</th>
                    <th className="text-center py-2 text-[10px] text-[#8C8780] font-medium">판정</th>
                  </tr>
                </thead>
                <tbody>
                  {sensitivityMatrix.map((row, i) => (
                    <tr
                      key={i}
                      className={`border-b border-[#F0EDE8] last:border-0 ${row.priceChangePercent === 0 ? "bg-[#C4956A]/5" : ""}`}
                    >
                      <td className="py-2.5">
                        <span className={`font-semibold ${
                          row.priceChangePercent > 0 ? "text-emerald-600" :
                          row.priceChangePercent < 0 ? "text-rose-500" : "text-[#1C1917]"
                        }`}>
                          {row.priceChangePercent > 0 ? "+" : ""}{row.priceChangePercent}%
                          {row.priceChangePercent === 0 && <span className="text-[10px] text-[#8C8780] ml-1">(현재)</span>}
                        </span>
                      </td>
                      <td className="py-2.5 text-right text-[#5C5650]">{fmtShort(row.adjustedMarketPrice)}</td>
                      <td className={`py-2.5 text-right font-semibold ${row.maxBidAtScenario < 0 ? "text-rose-500" : "text-[#1C1917]"}`}>
                        {fmtShort(row.maxBidAtScenario)}
                      </td>
                      <td className={`py-2.5 text-right font-semibold ${
                        row.roi >= 15 ? "text-emerald-600" :
                        row.roi >= 5 ? "text-[#C4956A]" : "text-rose-500"
                      }`}>
                        {row.roi === -999 ? "N/A" : `${row.roi >= 0 ? "+" : ""}${row.roi}%`}
                      </td>
                      <td className="py-2.5 text-center">
                        {row.isProfit ? (
                          <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] rounded font-medium">수익</span>
                        ) : (
                          <span className="px-1.5 py-0.5 bg-rose-100 text-rose-600 text-[10px] rounded font-medium">손실</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-[#B8B3AE] mt-2">
              * 시세 변동 시 취득세 기준 금액도 함께 조정됩니다
            </p>
          </motion.div>
        )}
      </div>

      {/* 공식 레퍼런스 */}
      <div className="bg-[#FAFAF8] rounded-xl border border-[#E8E5E0] p-4">
        <h4 className="text-[10px] font-bold text-[#8C8780] uppercase tracking-wide mb-2">역산 공식</h4>
        <div className="text-xs text-[#5C5650] leading-relaxed space-y-1">
          <p className="font-mono"><strong>P</strong> = V - M - C - R</p>
          <div className="text-[10px] text-[#B8B3AE] space-y-0.5 mt-1">
            <p>V = 급매 시세 | M = 목표 마진 | C = 제반비용 | R = 인수권리금</p>
            <p>Safe = P × 0.95 | Aggressive = P (한계선)</p>
          </div>
        </div>
      </div>

      {/* AI 시뮬레이터 연결 */}
      <Link
        to="/investment-simulator"
        className="block w-full bg-gradient-to-r from-[#1C1917] to-[#3A2E28] rounded-xl p-4 hover:from-[#2A2624] hover:to-[#4A3E38] transition-all duration-300 group"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-[#C4956A]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C4956A]/30 transition-colors">
            <i className="ri-brain-line text-[#C4956A] text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white leading-snug">
              AI 시뮬레이터로 심층 분석하기
            </p>
            <p className="text-xs text-[#B8B3AE] mt-0.5">
              표면이익 함정 제거 — 실제 순이익·ROE·투자 등급까지 30초 분석
            </p>
          </div>
          <div className="flex-shrink-0">
            <i className="ri-arrow-right-line text-[#C4956A] group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
          </div>
        </div>
      </Link>
    </div>
  );
}